import { SnapFlags, TICK_DT, newPlayerState, quantizeInputCmd, stepPlayer } from '@stillsun/shared';
import type { InputCmd, PlayerSnap, PlayerState, Vec3, World } from '@stillsun/shared';
import type { InputSample } from '../input/input.js';

/** Never emit more than this many catch-up steps in one frame (tab refocus, GC hitch). */
const MAX_CATCHUP_STEPS = 5;

/** Time constant for visually dissolving a reconciliation correction. */
const ERROR_DECAY_TAU = 0.035; // ~95% gone after 100 ms

/** Anything larger than this is a teleport/respawn — snap instead of smoothing. */
const MAX_SMOOTHED_ERROR = 2;

/**
 * Round an InputCmd through exactly the transform the wire codec applies. Predicting with
 * unquantised values guarantees a permanent standing correction, because the server never
 * sees the values we simulated with. The shared package owns the scales.
 */
export const quantizeCmd = quantizeInputCmd;

export class Predictor {
  readonly state: PlayerState;

  /** Commands sent but not yet acknowledged, ascending by seq. */
  private pending: InputCmd[] = [];
  private seq = 0;
  private acc = 0;
  /** Visual-only offset added to the rendered position, decaying to zero. */
  private error: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly outbox: InputCmd[] = [];

  /** Metres between the last snapshot and our prediction — useful HUD debug. */
  lastCorrection = 0;

  constructor(id: number, spawn: Vec3) {
    this.state = newPlayerState(id, spawn);
  }

  reset(id: number, spawn: Vec3): void {
    this.state.id = id;
    this.state.pos = { ...spawn };
    this.state.vel = { x: 0, y: 0, z: 0 };
    this.state.grounded = false;
    this.state.crouching = false;
    this.state.lastInputSeq = 0;
    this.pending.length = 0;
    this.outbox.length = 0;
    this.seq = 0;
    this.acc = 0;
    this.error = { x: 0, y: 0, z: 0 };
  }

  /**
   * Advances the fixed-step simulation. Returns the commands produced this frame,
   * which the caller should hand to NetClient.sendInputs().
   *
   * `live` must be false whenever the command will NOT reach the server (pre-Welcome,
   * or during a reconnect gap). Unsent commands must never enter `pending[]`: the
   * server would ack none of them, and the next Snapshot would replay a backlog of
   * input the server never simulated.
   */
  update(dt: number, sample: InputSample, world: World, live: boolean): readonly InputCmd[] {
    this.outbox.length = 0;
    this.acc += dt;

    let steps = 0;
    while (this.acc >= TICK_DT) {
      if (steps >= MAX_CATCHUP_STEPS) {
        this.acc = 0; // drop the backlog rather than firehosing the server
        break;
      }
      this.acc -= TICK_DT;
      steps += 1;

      this.seq = (this.seq + 1) >>> 0;
      const cmd = quantizeCmd({
        seq: this.seq,
        dt: TICK_DT,
        moveX: sample.moveX,
        moveY: sample.moveY,
        yaw: sample.yaw,
        buttons: sample.buttons,
        // A request, re-validated by the server every tick (PROTOCOL-V3 §1.2).
        targetId: sample.targetId,
      });
      stepPlayer(this.state, cmd, world);
      if (live) {
        this.pending.push(cmd);
        this.outbox.push(cmd);
      }
    }

    // Nothing is in flight while offline; drop anything left over from a dropped link.
    if (!live && this.pending.length > 0) this.pending.length = 0;

    // Keep the pending list bounded if the server goes quiet.
    if (this.pending.length > 256) this.pending.splice(0, this.pending.length - 256);

    // Decay the visual correction offset.
    const k = Math.exp(-dt / ERROR_DECAY_TAU);
    this.error.x *= k;
    this.error.y *= k;
    this.error.z *= k;

    return this.outbox;
  }

  /**
   * Applies server authority and replays unacknowledged input.
   *
   * What v3 corrects, and what it deliberately does not (PROTOCOL-V3 §3.1, §7.2):
   *
   * - **`pos`, `vel`, `hp` and `grounded`** come from the snapshot. Replaying the unacked ticks
   *   without them diverges on the first step.
   * - **`yaw` never does.** `PlayerSnap.yaw` is a u8 (~0.0123 rad); reconciling from it would
   *   twitch the body on every correction. The body yaw is the client's, from `lookYaw`.
   * - **`stamina` never does either.** The sim keeps it on a 0.01 grid because sprint drains a
   *   third of a unit a tick; the wire carries a rounded whole number. Predict it locally.
   * - **`regenTicks`, `groundTicks`, `speedMult`, `staminaMax` and `stance` are no longer on the
   *   wire at all.** They are small integers that converge on their own inside a second, and six
   *   bytes x 30 Hz x 500 players is not defensible at city scale. Do not re-add them.
   */
  reconcile(snap: PlayerSnap, lastInputSeq: number, world: World, lookYaw: number): void {
    const predicted = { ...this.state.pos };

    this.state.pos = { ...snap.pos };
    this.state.vel = { ...snap.vel };
    this.state.hp = snap.hp;
    // `grounded` gates the jump; replaying without it either eats a jump or grants a second one.
    // `crouching` and `winded` are *not* taken from their flags: `stepPlayer` derives both from
    // state the wire no longer carries (stance, stamina), so a snapshot value would be undone by
    // the very next replayed step and only ever disagrees for one frame.
    this.state.grounded = (snap.flags & SnapFlags.Grounded) !== 0;
    this.state.lastInputSeq = lastInputSeq;

    if (this.pending.length > 0) {
      this.pending = this.pending.filter((c) => c.seq > lastInputSeq);
      for (const c of this.pending) stepPlayer(this.state, c, world);
    }

    // The player owns their own facing; the snapshot's coarse yaw byte is only advisory.
    this.state.yaw = lookYaw;

    const dx = predicted.x + this.error.x - this.state.pos.x;
    const dy = predicted.y + this.error.y - this.state.pos.y;
    const dz = predicted.z + this.error.z - this.state.pos.z;
    const dist = Math.hypot(dx, dy, dz);
    this.lastCorrection = Math.hypot(
      predicted.x - this.state.pos.x,
      predicted.y - this.state.pos.y,
      predicted.z - this.state.pos.z,
    );
    if (dist > MAX_SMOOTHED_ERROR) {
      this.error = { x: 0, y: 0, z: 0 }; // respawn/teleport: snap
    } else {
      this.error = { x: dx, y: dy, z: dz };
    }
  }

  /** Feet position to render the camera from, including the smoothed correction. */
  renderPos(out: Vec3): Vec3 {
    out.x = this.state.pos.x + this.error.x;
    out.y = this.state.pos.y + this.error.y;
    out.z = this.state.pos.z + this.error.z;
    return out;
  }

  get pendingCount(): number {
    return this.pending.length;
  }
}
